import { If, splitProps, JSX } from 'cdui-js';
import './style.css';

export type CheckBoxCardProps = JSX.HTMLAttributes<never> & {
  active: boolean;
  iconWidth?: number;
  iconHeight?: number;
  /** 选中时是否展示右下角小勾，默认 true */
  showBadge?: boolean;
  /** 选中边框色，默认 var(--check-border-active) */
  activeBorderColor?: string;
};

type CheckBadgeProps = {
  iconWidth: number;
  iconHeight: number;
};

const CheckBadge = (badgeProps: CheckBadgeProps) => (
  <svg
    class="checkbox-card-badge"
    width={badgeProps.iconWidth}
    height={badgeProps.iconHeight}
    viewBox="0 0 11 13"
    fill="none"
    stroke="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      class="checkbox-card-badge-bg"
      d="M10.7615 0V6.31409C10.7615 9.6278 8.07524 12.3141 4.76153 12.3141H0L0.617774 6.67532C0.971902 3.443 3.50525 0.880141 6.73326 0.488604L10.7615 0Z"
    />
    <path
      class="checkbox-card-badge-mark"
      d="M8.82391 3.83984L5.59185 8.12282C5.22591 8.59155 5.22591 8.59155 4.81561 8.12282L3.41797 6.52734"
      stroke-width="1.5"
      stroke-miterlimit="10"
      fill="none"
    />
  </svg>
);

const CheckBadgeRounded = (badgeProps: CheckBadgeProps) => (
  <svg
    class="checkbox-card-badge"
    width={badgeProps.iconWidth}
    height={badgeProps.iconHeight}
    viewBox="0 0 19 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      class="checkbox-card-badge-bg"
      d="M18.7161 0V5.81152C18.7161 12.4389 13.3435 17.8115 6.71607 17.8115H0L1.4021 7.16781C1.8262 3.94838 4.40871 1.4433 7.63957 1.11738L18.7161 0Z"
    />
    <path
      class="checkbox-card-badge-mark"
      d="M15.3462 5.55469L9.72507 11.7497C9.08864 12.4277 9.08864 12.4277 8.37506 11.7497L5.94434 9.44198"
      stroke-width="1.5"
      stroke-miterlimit="10"
      fill="none"
    />
  </svg>
);

export const CheckBoxCard = <T, U extends JSX.Element>(props: CheckBoxCardProps) => {
  const [localProps, restProps] = splitProps(props, [
    'class',
    'children',
    'active',
    'style',
    'iconWidth',
    'iconHeight',
    'showBadge',
    'activeBorderColor',
  ]);

  const cardClass = localProps.class ?? '';
  const useRoundedBadge = cardClass.indexOf('round-') !== -1;
  const showBadge = localProps.showBadge !== false;
  const activeBorder = localProps.activeBorderColor ?? 'var(--check-border-active)';

  return (
    <div
      style={{
        ...(localProps.style as JSX.CSSProperties),
        position: 'relative',
        border: localProps.active ? `1px solid ${activeBorder}` : '1px solid var(--check-border)',
        background: 'var(--bg-muted)',
        overflow: 'hidden',
        cursor: 'pointer',
        'user-select': 'none',
        '-webkit-tap-highlight-color': 'transparent',
      }}
      class={`relative ${cardClass} ${useRoundedBadge ? '' : 'round'}`.trim()}
      {...restProps}
    >
      {props.children}

      <If when={localProps.active && showBadge}>
        {useRoundedBadge ? (
          <CheckBadgeRounded iconWidth={localProps.iconWidth ?? 19} iconHeight={localProps.iconHeight ?? 18} />
        ) : (
          <CheckBadge iconWidth={localProps.iconWidth ?? 11} iconHeight={localProps.iconHeight ?? 13} />
        )}
      </If>
    </div>
  );
};
